/**
 * Visit Types
 * Types for visit-related data matching backend schema
 */

import { DateString } from './common'

export interface Visit {
  id: string  // UUID from backend

  // Office and user information
  office_id?: number | null
  user_id?: string | null  // UUID

  // Client information
  client_id: string  // UUID (required)
  client_info?: Record<string, any> | null  // JSONB snapshot

  // Visit timing
  visit_date?: DateString | null
  time_in?: string | null  // Time string (HH:MM:SS)
  time_out?: string | null  // Time string (HH:MM:SS)

  // Status
  status?: number | null  // 1=scheduled, 2=in_progress, 3=completed, 4=cancelled

  // Human-readable fields (from backend)
  control_number?: string | null
  lawyer_name?: string | null
  office_name?: string | null

  // Related data
  transactions?: VisitTransaction[]
  classifications?: VisitClassification[]

  // Metadata (matches legacy DB column names)
  date_created: DateString
  date_updated: DateString
}

export interface VisitCreate {
  // Office and user information
  office_id?: number | null
  user_id?: string | null

  // Client information (required)
  client_id: string
  client_info?: Record<string, any> | null

  // Visit timing
  visit_date?: DateString | null
  time_in?: string | null
  time_out?: string | null

  // Status
  status?: number | null
}

export interface VisitUpdate extends Partial<VisitCreate> {}

export interface VisitFilters {
  search?: string
  client_id?: string
  office_id?: number
  legal_assistance_type_id?: number
  status?: number
  date_from?: DateString
  date_to?: DateString
}

export type VisitListItem = Pick<Visit,
  'id' | 'client_id' | 'visit_date' | 'time_in' | 'status' | 'office_id' | 'user_id' | 'client_info' | 'date_created'
> & {
  control_number?: string | null
  lawyer_name?: string | null
  assistance_type_description?: string | null
}

// Visit status constants
export enum VisitStatus {
  SCHEDULED = 1,
  IN_PROGRESS = 2,
  COMPLETED = 3,
  CANCELLED = 4,
}

export const VISIT_STATUS_LABELS: Record<number, string> = {
  [VisitStatus.SCHEDULED]: 'Scheduled',
  [VisitStatus.IN_PROGRESS]: 'In Progress',
  [VisitStatus.COMPLETED]: 'Completed',
  [VisitStatus.CANCELLED]: 'Cancelled',
}

// Visit Classification types
export interface VisitClassification {
  id: string
  visit_id: string
  classification_id: number
  detail?: Record<string, any> | null
  date_created: DateString
  date_updated: DateString
}

export interface VisitClassificationCreate {
  visit_id: string
  classification_id: number
  detail?: Record<string, any> | null
}

// Visit Transaction types
export interface VisitTransaction {
  id: string
  visit_id: string
  control_number?: string | null
  transaction_type_id?: number | null
  transaction_detail?: Record<string, any> | null
  date_created: DateString
  date_updated: DateString
}

export interface VisitTransactionCreate {
  visit_id: string
  control_number?: string | null
  transaction_type_id?: number | null
  transaction_detail?: Record<string, any> | null
}

export interface VisitTransactionUpdate {
  transaction_type_id?: number | null
  transaction_detail?: Record<string, any> | null
}
